import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  EMPTY_VENDOR_BRANDING,
  VendorBranding,
  VendorOfferShell,
} from './VendorOfferChrome';
import NhsTrialFlow from './NhsTrialQuestionnaire';
import './VendorOffer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const IOS_APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL ||
  'https://apps.apple.com/us/app/heal-music-breath/id6746941693';

type ClaimStatus = 'idle' | 'submitting' | 'success' | 'error';
type TokenStatus = 'checking' | 'valid' | 'invalid';

function FormattedBody({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="vendor-offer-subtitle">
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </p>
  );
}

const VendorOffer: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const normalizedToken = useMemo(() => (token || '').trim(), [token]);

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<ClaimStatus>('idle');
  const [error, setError] = useState('');
  const [branding, setBranding] = useState<VendorBranding>(EMPTY_VENDOR_BRANDING);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('checking');
  const [tokenCheckError, setTokenCheckError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const checkToken = async () => {
      if (!normalizedToken) {
        setTokenStatus('invalid');
        setTokenCheckError('This offer link is missing or invalid.');
        return;
      }

      setTokenStatus('checking');
      setTokenCheckError('');

      try {
        const encoded = encodeURIComponent(normalizedToken);
        const response = await fetch(`${API_URL}/vendor-offers/tokens/${encoded}`);

        if (cancelled) return;

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          const detail =
            typeof data?.detail === 'string'
              ? data.detail
              : 'This offer link is invalid or has expired.';
          setTokenStatus('invalid');
          setTokenCheckError(detail);
          return;
        }

        const data = await response.json();
        if (!cancelled) {
          setBranding({
            vendorName: data?.vendor_name ?? null,
            logoUrl: data?.logo_url ?? null,
            headline: data?.headline ?? null,
            bodyText: data?.body_text ?? null,
            successText: data?.success_text ?? null,
            questionnaireSlug: data?.questionnaire_slug ?? null,
          });
          setTokenStatus('valid');
        }
      } catch {
        if (!cancelled) {
          setTokenStatus('invalid');
          setTokenCheckError('We couldn’t verify this offer. Check your connection and try again.');
        }
      }
    };

    checkToken();
    return () => {
      cancelled = true;
    };
  }, [normalizedToken]);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!normalizedToken) {
      setStatus('error');
      setError('Invalid or missing offer token.');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setStatus('submitting');

      const response = await fetch(`${API_URL}/vendor-offers/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: normalizedToken,
          email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        setStatus('error');
        setError(errorData?.detail || 'Failed to claim this offer.');
        return;
      }

      const data = await response.json();
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      setBranding((prev) => ({
        ...prev,
        vendorName: data?.vendor_name ?? prev.vendorName,
      }));
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  };

  if (tokenStatus === 'checking') {
    return (
      <VendorOfferShell branding={EMPTY_VENDOR_BRANDING}>
        <div className="vendor-offer-main">
          <p className="vendor-offer-subtitle vendor-offer-loading">Checking your offer link…</p>
        </div>
      </VendorOfferShell>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <VendorOfferShell branding={EMPTY_VENDOR_BRANDING}>
        <div className="vendor-offer-main">
          <h1 className="vendor-offer-title">Offer not found</h1>
          <p className="vendor-offer-error-screen">{tokenCheckError}</p>
          <p className="vendor-offer-subtitle">
            If you scanned a QR code or opened a partner link, ask them for an updated link.
          </p>
        </div>
        <a className="vendor-offer-back" href="/">
          Back to Heal’s website
        </a>
      </VendorOfferShell>
    );
  }

  if (branding.questionnaireSlug) {
    return <NhsTrialFlow token={normalizedToken} branding={branding} />;
  }

  const headline = branding.headline || 'Unlock your vendor offer';
  const bodyText =
    branding.bodyText || 'Enter your email to link this offer to your Heal account.';
  const successText =
    branding.successText ||
    (branding.vendorName
      ? `Download Heal’s iOS app, sign in with the email you entered, and you’ll see your ${branding.vendorName} offer.`
      : 'Download Heal’s iOS app, sign in with the email you entered, and you’ll see your partner offer.');

  return (
    <VendorOfferShell branding={branding}>
      {status !== 'success' ? (
        <>
          <div className="vendor-offer-main">
            <h1 className="vendor-offer-title">{headline}</h1>
            <FormattedBody text={bodyText} />

            <form onSubmit={handleSubmit} className="vendor-offer-form">
              <input
                type="email"
                name="email"
                placeholder="Enter Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'submitting'}
                autoComplete="email"
                required
              />

              {status === 'error' && error && <p className="vendor-offer-error">{error}</p>}

              <button
                type="submit"
                className={`vendor-offer-submit ${status === 'submitting' ? 'disabled' : ''}`}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Linking…' : 'Continue'}
              </button>
            </form>
          </div>
          <a className="vendor-offer-back" href="/">
            Back to Heal’s website
          </a>
        </>
      ) : (
        <>
          <div className="vendor-offer-main">
            <h1 className="vendor-offer-title">Offer Linked</h1>
            <p className="vendor-offer-subtitle">{successText}</p>
            <a
              className="vendor-offer-submit vendor-offer-submit-link"
              href={IOS_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in the App Store
            </a>
          </div>
          <a className="vendor-offer-back" href="/">
            Back to Heal’s website
          </a>
        </>
      )}
    </VendorOfferShell>
  );
};

export default VendorOffer;
